import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/Layout';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, Loading, Select, Stat, Textarea } from '@/components/ui';
import { api } from '@/lib/api';
import { fmtWhen } from '@/lib/format';
import { canEdit, rk, useMe } from '@/lib/hooks';

type IssueRow = { number: number; title: string; url: string; createdAt: string; labels: string[] };
type Delivery =
  | { configured: false }
  | {
      configured: true;
      repo: string;
      pulls: Array<{ number: number; title: string; url: string; branch: string; tool: string; author: string | null; draft: boolean; createdAt: string; ci: 'success' | 'failure' | 'pending' | 'none' }>;
      issues: Record<string, IssueRow[]>;
      groups: Array<{ key: string; title: string; attention: boolean; label: string }>;
      health: { ci: { state: string; url: string; at: string } | null; e2e: { state: string; url: string; at: string } | null };
      lessons: Array<{ id: string; title: string; seen: number; guard: string }>;
      mergedLast30Days: Record<string, number>;
      fetchedAt: string;
    };

const CI_TONE = { success: 'ok', failure: 'bad', pending: 'warn', none: 'neutral' } as const;
const runTone = (s?: string) => (s === 'success' ? 'ok' : s === 'failure' || s === 'cancelled' || s === 'timed_out' ? 'bad' : 'warn');

const IssueList = ({ rows }: { rows: IssueRow[] }) => (
  <ul>
    {rows.map((i) => (
      <li key={i.number} className="border-b border-line-2 px-4 py-2 text-[13px] last:border-0">
        <a href={i.url} target="_blank" rel="noreferrer" className="hover:underline">
          #{i.number} {i.title}
        </a>
        <span className="ml-2 text-xs text-ink-3">{fmtWhen(i.createdAt)}</span>
      </li>
    ))}
  </ul>
);

const RequestChange = () => {
  const qc = useQueryClient();
  const [kind, setKind] = useState<'fix' | 'style'>('style');
  const [where, setWhere] = useState('');
  const [what, setWhat] = useState('');
  const send = useMutation({
    mutationFn: () => api<{ number: number; url: string }>('/v1/delivery/requests', { body: { kind, where, what } }),
    onSuccess: (r) => {
      toast.success(`Filed #${r.number} for an agent`);
      setWhat('');
      setWhere('');
      void qc.invalidateQueries({ queryKey: rk('delivery') });
    },
  });
  return (
    <Card title="Request a change">
      <div className="flex flex-col gap-3 p-4">
        <Field label="Kind">
          <Select value={kind} onChange={(e) => setKind(e.target.value as 'fix' | 'style')}>
            <option value="style">Style (colours, spacing, layout, wording)</option>
            <option value="fix">Fix something that’s broken</option>
          </Select>
        </Field>
        <Field label="Where" hint="Page URL or screen name">
          <Input value={where} onChange={(e) => setWhere(e.target.value)} placeholder="https://apply.bloomnow.me/employer" />
        </Field>
        <Field label="What should change">
          <Textarea rows={4} value={what} onChange={(e) => setWhat(e.target.value)} placeholder="The Next button is hard to see on phones — make it the primary colour." />
        </Field>
        <Button variant="primary" className="self-start" disabled={!what.trim()} loading={send.isPending} onClick={() => send.mutate()}>
          <Send className="size-3.5" /> File for an agent
        </Button>
        {send.error ? <ErrorNote error={send.error} /> : null}
        <p className="text-xs text-ink-3">Opens a GitHub issue. An agent prepares a pull request (style changes come with before/after screenshots); a person approves it. Nothing changes on live directly.</p>
      </div>
    </Card>
  );
};

/** Control board: what the delivery agents are doing (read-only), and a way to ask them for a change. */
export const Delivery = () => {
  const me = useMe();
  const q = useQuery({ queryKey: rk('delivery'), queryFn: () => api<Delivery>('/v1/delivery'), refetchInterval: 60_000 });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const d = q.data!;
  if (!d.configured)
    return (
      <>
        <PageHeader title="Delivery" subtitle="The delivery agents at work: pull requests, incidents, tests and lessons." />
        <Empty title="Not connected to GitHub yet">Set AGENT_MANAGER_GITHUB_TOKEN (fine-grained, read-only apart from issues) for this deployment.</Empty>
      </>
    );
  const attention = d.groups.filter((g) => g.attention && d.issues[g.key]?.length);
  const queues = d.groups.filter((g) => !g.attention);
  return (
    <>
      <PageHeader
        title="Delivery"
        subtitle={`The delivery agents at work in ${d.repo}. Read-only: merging and releasing stay with people on GitHub.`}
        actions={<span className="text-xs text-ink-3">Updated {fmtWhen(d.fetchedAt)}</span>}
      />
      <div className="mb-5 grid grid-cols-2 gap-5 rounded-lg border border-line bg-panel p-4 sm:grid-cols-4">
        <Stat label="Open pull requests" value={d.pulls.length} />
        <Stat label="Need attention" value={attention.reduce((a, g) => a + d.issues[g.key].length, 0)} tone={attention.length ? 'bad' : undefined} />
        <Stat
          label="CI on development"
          value={
            d.health.ci ? (
              <a href={d.health.ci.url} target="_blank" rel="noreferrer">
                <Badge tone={runTone(d.health.ci.state)}>{d.health.ci.state}</Badge>
              </a>
            ) : (
              '—'
            )
          }
        />
        <Stat
          label="Nightly staging tests"
          value={
            d.health.e2e ? (
              <a href={d.health.e2e.url} target="_blank" rel="noreferrer">
                <Badge tone={runTone(d.health.e2e.state)}>{d.health.e2e.state}</Badge>
              </a>
            ) : (
              '—'
            )
          }
        />
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex min-w-0 flex-col gap-5">
          {attention.map((g) => (
            <Card key={g.key} title={<span className="text-bad">{g.title}</span>}>
              <IssueList rows={d.issues[g.key]} />
            </Card>
          ))}
          <Card title="Open pull requests">
            {!d.pulls.length ? (
              <p className="px-4 py-3 text-[13px] text-ink-3">None open.</p>
            ) : (
              <ul>
                {d.pulls.map((p) => (
                  <li key={p.number} className="flex flex-wrap items-center gap-2 border-b border-line-2 px-4 py-2 text-[13px] last:border-0">
                    <Badge tone={CI_TONE[p.ci]}>{p.ci === 'none' ? 'no CI' : p.ci}</Badge>
                    <a href={p.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate hover:underline">
                      #{p.number} {p.title}
                    </a>
                    <Badge>{p.tool}</Badge>
                    {p.draft && <Badge>draft</Badge>}
                    <span className="text-xs text-ink-3">{fmtWhen(p.createdAt)}</span>
                    <ExternalLink className="size-3 text-ink-3" />
                  </li>
                ))}
              </ul>
            )}
          </Card>
          {queues.map((g) =>
            d.issues[g.key]?.length ? (
              <Card key={g.key} title={`${g.title} (${d.issues[g.key].length})`}>
                <IssueList rows={d.issues[g.key]} />
              </Card>
            ) : null,
          )}
        </div>

        <div className="flex flex-col gap-5">
          {canEdit(me.data) && <RequestChange />}
          <Card title="Merged in the last 30 days">
            <ul className="p-4 text-[13px]">
              {Object.entries(d.mergedLast30Days).map(([tool, n]) => (
                <li key={tool} className="flex justify-between py-0.5">
                  <span>{tool}</span>
                  <span className="tabular-nums">{n}</span>
                </li>
              ))}
            </ul>
          </Card>
          <Card title="Lesson book">
            {!d.lessons.length ? (
              <p className="px-4 py-3 text-[13px] text-ink-3">No lessons found.</p>
            ) : (
              <ul>
                {d.lessons.map((l) => (
                  <li key={l.id} className="border-b border-line-2 px-4 py-2 text-[13px] last:border-0">
                    <div className="flex items-start justify-between gap-2">
                      <span>
                        <span className="text-ink-3">{l.id}</span> {l.title}
                      </span>
                      {l.seen > 1 && <Badge tone="warn">seen {l.seen}×</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </>
  );
};
